/**
 * Angular service for OTP (One-Time Password) API operations
 * 
 * @remarks
 * Provides comprehensive methods for OTP-based authentication including:
 * - OTP generation and delivery (email/SMS)
 * - OTP verification with attempt tracking
 * - Status checking and polling
 * - Resend functionality
 * 
 * All methods return RxJS Observables for reactive programming patterns.
 * Authentication is handled by HTTP interceptors in the consuming application.
 * 
 * **Rate Limits:**
 * - Send/Resend: 5 requests per 15 minutes per IP
 * - Verify: 10 requests per 15 minutes per IP
 * - Status: 20 requests per 15 minutes per IP
 * 
 * **OTP Properties:**
 * - Expiration: 10 minutes from creation
 * - Max Attempts: 5 verification attempts
 * - Code Length: 6-8 digits (default: 6)
 * - Auto-deletion: 1 hour after expiration
 * 
 * @example
 * ```typescript
 * // In your module or standalone component
 * import { provideUserManagementLib } from 'user-management-lib';
 * 
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideUserManagementLib({
 *       apiBaseUrl: environment.apiBaseUrl
 *     })
 *   ]
 * };
 * 
 * // In your component
 * constructor(private otpService: OtpService) {}
 * 
 * // Send OTP
 * this.otpService.sendOtp({
 *   recipient: 'user@example.com',
 *   deliveryMethod: 'EMAIL',
 *   purpose: 'login'
 * }).subscribe(response => {
 *   console.log('OTP sent:', response.data);
 * });
 * 
 * // Verify OTP
 * this.otpService.verifyOtp({
 *   recipient: 'user@example.com',
 *   code: '123456'
 * }).subscribe({
 *   next: response => console.log('Verified!'),
 *   error: err => console.log('Failed:', err.attemptsRemaining)
 * });
 * ```
 * 
 * @see OTP_API_REFERENCE.md for complete API documentation
 */

import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, timer, takeWhile, switchMap, map, catchError } from 'rxjs';
import { tap } from 'rxjs/operators';
import { USER_MANAGEMENT_API_CONFIG } from '../config/user-management-config';
import {
  SendOtpDto,
  VerifyOtpDto,
  GetOtpStatusQueryParams,
  SendOtpResponse,
  VerifyOtpSuccessResponse,
  VerifyOtpFailureResponse,
  OtpStatusResponse,
  OtpNotFoundResponse,
  OtpVerificationError,
  OtpPollingOptions,
  OtpStatus,
  OtpDeliveryMethod,
  OtpPurpose
} from '../models/otp.model';

/**
 * OTP API Service
 * 
 * @remarks
 * Manages all OTP-related operations with rate limiting, auto-expiration,
 * and comprehensive error handling.
 */
@Injectable({
  providedIn: 'root'
})
export class OtpService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(USER_MANAGEMENT_API_CONFIG);
  private readonly baseUrl = `${this.config.apiBaseUrl}/api/otp`;

  /**
   * Signal for tracking last OTP send time (for client-side rate limit hints)
   */
  private readonly lastSendTime = signal<number | null>(null);

  /**
   * Computed signal for checking if enough time has passed since last send
   * (5 sends per 15 minutes = ~3 minutes between sends as a suggestion)
   */
  readonly canSendOtp = computed(() => {
    const lastSend = this.lastSendTime();
    if (!lastSend) return true;
    const threeMinutes = 3 * 60 * 1000;
    return Date.now() - lastSend > threeMinutes;
  });

  /**
   * Log debug messages if debug mode is enabled
   */
  private log(message: string, ...args: any[]): void {
    if (this.config.debug) {
      console.log(`[OtpService] ${message}`, ...args);
    }
  }

  /**
   * Parse verification error message to extract attempts remaining
   */
  private parseVerificationError(error: HttpErrorResponse): OtpVerificationError {
    const errorResponse = error.error as VerifyOtpFailureResponse;
    const message = errorResponse?.message || error.message;
    
    // Try to extract attempts remaining from message like "Invalid OTP code. 3 attempts remaining."
    const attemptsMatch = message.match(/(\d+)\s+attempts?\s+remaining/i);
    const attemptsRemaining = attemptsMatch ? parseInt(attemptsMatch[1], 10) : undefined;
    
    // Check if locked (no attempts remaining or explicit lock message)
    const isLocked = attemptsRemaining === 0 || 
                     /locked|blocked|exceeded|maximum attempts/i.test(message);

    return {
      message,
      attemptsRemaining,
      isLocked,
      originalError: errorResponse
    };
  }

  /**
   * Send OTP to recipient via email or SMS
   * 
   * @remarks
   * Rate limited to 5 requests per 15 minutes per IP.
   * OTP expires after 10 minutes.
   * 
   * @param dto - OTP send request data
   * @returns Observable of OTP send response with expiration time
   * 
   * @throws {HttpErrorResponse} 400 - Validation error (invalid email/phone format)
   * @throws {HttpErrorResponse} 429 - Rate limit exceeded
   * @throws {HttpErrorResponse} 500 - Server error
   * 
   * @example
   * ```typescript
   * this.otpService.sendOtp({
   *   recipient: 'user@example.com',
   *   deliveryMethod: 'EMAIL',
   *   purpose: 'registration',
   *   otpLength: 6
   * }).subscribe({
   *   next: response => {
   *     console.log('OTP sent to:', response.data.recipient);
   *     console.log('Expires at:', response.data.expiresAt);
   *   },
   *   error: err => {
   *     if (err.status === 429) {
   *       console.log('Rate limit exceeded');
   *     }
   *   }
   * });
   * ```
   */
  sendOtp(dto: SendOtpDto): Observable<SendOtpResponse> {
    this.log('Sending OTP', dto);
    return this.http.post<SendOtpResponse>(this.baseUrl + '/send', dto).pipe(
      tap(response => {
        this.lastSendTime.set(Date.now());
        this.log('OTP sent successfully', response.data);
      })
    );
  }

  /**
   * Verify OTP code
   * 
   * @remarks
   * Rate limited to 10 requests per 15 minutes per IP.
   * Maximum 5 verification attempts per OTP.
   * Returns structured error with attempts remaining on failure.
   * 
   * @param dto - OTP verification data
   * @returns Observable of verification success response
   * 
   * @throws {OtpVerificationError} Verification failed with attempts info
   * @throws {HttpErrorResponse} 429 - Rate limit exceeded
   * @throws {HttpErrorResponse} 500 - Server error
   * 
   * @example
   * ```typescript
   * this.otpService.verifyOtp({
   *   recipient: 'user@example.com',
   *   code: '123456',
   *   purpose: 'login'
   * }).subscribe({
   *   next: response => {
   *     console.log('Verified at:', response.data.verifiedAt);
   *     // Proceed with authentication
   *   },
   *   error: (err: OtpVerificationError) => {
   *     console.log('Verification failed:', err.message);
   *     console.log('Attempts remaining:', err.attemptsRemaining);
   *     if (err.isLocked) {
   *       console.log('OTP locked due to too many attempts');
   *     }
   *   }
   * });
   * ```
   */
  verifyOtp(dto: VerifyOtpDto): Observable<VerifyOtpSuccessResponse> {
    this.log('Verifying OTP', { recipient: dto.recipient, purpose: dto.purpose });
    return this.http.post<VerifyOtpSuccessResponse>(this.baseUrl + '/verify', dto).pipe(
      tap(response => this.log('OTP verified successfully', response.data)),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 400) {
          const parsedError = this.parseVerificationError(error);
          this.log('OTP verification failed', parsedError);
          return throwError(() => parsedError);
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * Resend OTP to recipient
   * 
   * @remarks
   * Rate limited to 5 requests per 15 minutes per IP.
   * Invalidates any existing pending OTPs before sending new one.
   * Same functionality as sendOtp but explicitly for resend use case.
   * 
   * @param dto - OTP send request data
   * @returns Observable of OTP send response
   * 
   * @example
   * ```typescript
   * this.otpService.resendOtp({
   *   recipient: 'user@example.com',
   *   deliveryMethod: 'EMAIL'
   * }).subscribe(response => {
   *   console.log('New OTP sent');
   * });
   * ```
   */
  resendOtp(dto: SendOtpDto): Observable<SendOtpResponse> {
    this.log('Resending OTP', dto);
    return this.http.post<SendOtpResponse>(this.baseUrl + '/resend', dto).pipe(
      tap(response => {
        this.lastSendTime.set(Date.now());
        this.log('OTP resent successfully', response.data);
      })
    );
  }

  /**
   * Get OTP status for a recipient
   * 
   * @remarks
   * Rate limited to 20 requests per 15 minutes per IP.
   * Returns current status, attempts made, and expiration time.
   * 
   * @param recipient - Email or phone number
   * @param params - Optional query parameters (purpose filter)
   * @returns Observable of OTP status response
   * 
   * @throws {HttpErrorResponse} 404 - No OTP found for recipient
   * @throws {HttpErrorResponse} 429 - Rate limit exceeded
   * 
   * @example
   * ```typescript
   * this.otpService.getOtpStatus('user@example.com').subscribe({
   *   next: response => {
   *     const status = response.data;
   *     console.log('Status:', status.status);
   *     console.log('Attempts:', status.attempts, '/', status.maxAttempts);
   *     console.log('Expires at:', status.expiresAt);
   *   },
   *   error: err => {
   *     if (err.status === 404) {
   *       console.log('No active OTP found');
   *     }
   *   }
   * });
   * ```
   */
  getOtpStatus(recipient: string, params?: GetOtpStatusQueryParams): Observable<OtpStatusResponse> {
    this.log('Getting OTP status', { recipient, params });

    let httpParams = new HttpParams();
    if (params?.purpose) {
      httpParams = httpParams.set('purpose', params.purpose);
    }

    return this.http.get<OtpStatusResponse>(`${this.baseUrl}/status/${encodeURIComponent(recipient)}`, { 
      params: httpParams 
    }).pipe(
      tap(response => this.log('OTP status retrieved', response.data))
    );
  }

  /**
   * Poll OTP status until verification or expiration
   * 
   * @remarks
   * Continuously polls the status endpoint at specified intervals until:
   * - OTP is verified
   * - OTP expires
   * - OTP fails
   * - Maximum polling duration is reached
   * 
   * Useful for scenarios where you want to wait for OTP verification
   * (e.g., showing a loading spinner while user enters code).
   * 
   * @param recipient - Email or phone number
   * @param options - Polling configuration
   * @returns Observable that emits status updates and completes when done
   * 
   * @example
   * ```typescript
   * this.otpService.pollOtpStatus('user@example.com', {
   *   interval: 2000,      // Check every 2 seconds
   *   maxDuration: 600000  // Stop after 10 minutes
   * }).subscribe({
   *   next: response => {
   *     console.log('Current status:', response.data.status);
   *     if (response.data.status === 'VERIFIED') {
   *       console.log('User verified OTP!');
   *     }
   *   },
   *   complete: () => console.log('Polling stopped')
   * });
   * ```
   */
  pollOtpStatus(
    recipient: string,
    options: OtpPollingOptions = {}
  ): Observable<OtpStatusResponse> {
    const interval = options.interval ?? 2000; // Default: 2 seconds
    const maxDuration = options.maxDuration ?? 600000; // Default: 10 minutes
    const stopOnStatus = options.stopOnStatus ?? ['VERIFIED', 'EXPIRED', 'FAILED'];

    this.log('Starting OTP status polling', { recipient, interval, maxDuration });

    const startTime = Date.now();

    return timer(0, interval).pipe(
      switchMap(() => this.getOtpStatus(recipient)),
      takeWhile(response => {
        const elapsed = Date.now() - startTime;
        const shouldContinue = !stopOnStatus.includes(response.data.status) && elapsed < maxDuration;
        
        if (!shouldContinue) {
          this.log('Polling stopped', { 
            status: response.data.status, 
            elapsed,
            reason: stopOnStatus.includes(response.data.status) ? 'final status' : 'max duration'
          });
        }
        
        return shouldContinue;
      }, true), // inclusive: emit the final value before completing
      tap(response => this.log('Poll update', response.data.status))
    );
  }

  // ============================================================================
  // Convenience Methods
  // ============================================================================

  /**
   * Send OTP via email
   * 
   * @param email - Recipient email address
   * @param purpose - Optional purpose/context
   * @param otpLength - Optional code length (6-8 digits)
   * @returns Observable of send response
   * 
   * @example
   * ```typescript
   * this.otpService.sendEmailOtp('user@example.com', 'login')
   *   .subscribe(response => console.log('Email OTP sent'));
   * ```
   */
  sendEmailOtp(email: string, purpose?: OtpPurpose, otpLength?: number): Observable<SendOtpResponse> {
    this.log('Sending email OTP', { email, purpose });
    return this.sendOtp({
      recipient: email,
      deliveryMethod: 'EMAIL',
      purpose,
      otpLength
    });
  }

  /**
   * Send OTP via SMS
   * 
   * @param phone - Recipient phone number
   * @param purpose - Optional purpose/context
   * @param otpLength - Optional code length (6-8 digits)
   * @returns Observable of send response
   * 
   * @example
   * ```typescript
   * this.otpService.sendSmsOtp('+1234567890', 'registration')
   *   .subscribe(response => console.log('SMS OTP sent'));
   * ```
   */
  sendSmsOtp(phone: string, purpose?: OtpPurpose, otpLength?: number): Observable<SendOtpResponse> {
    this.log('Sending SMS OTP', { phone, purpose });
    return this.sendOtp({
      recipient: phone,
      deliveryMethod: 'SMS',
      purpose,
      otpLength
    });
  }

  /**
   * Resend OTP via email
   * 
   * @param email - Recipient email address
   * @param purpose - Optional purpose/context
   * @returns Observable of send response
   */
  resendEmailOtp(email: string, purpose?: OtpPurpose): Observable<SendOtpResponse> {
    this.log('Resending email OTP', { email, purpose });
    return this.resendOtp({
      recipient: email,
      deliveryMethod: 'EMAIL',
      purpose
    });
  }

  /**
   * Resend OTP via SMS
   * 
   * @param phone - Recipient phone number
   * @param purpose - Optional purpose/context
   * @returns Observable of send response
   */
  resendSmsOtp(phone: string, purpose?: OtpPurpose): Observable<SendOtpResponse> {
    this.log('Resending SMS OTP', { phone, purpose });
    return this.resendOtp({
      recipient: phone,
      deliveryMethod: 'SMS',
      purpose
    });
  }

  /**
   * Quick verification without purpose
   * 
   * @param recipient - Email or phone number
   * @param code - OTP code
   * @returns Observable of verification response
   * 
   * @example
   * ```typescript
   * this.otpService.quickVerify('user@example.com', '123456')
   *   .subscribe(() => console.log('Verified!'));
   * ```
   */
  quickVerify(recipient: string, code: string): Observable<VerifyOtpSuccessResponse> {
    return this.verifyOtp({ recipient, code });
  }

  /**
   * Check if OTP is verified
   * 
   * @param recipient - Email or phone number
   * @returns Observable that emits true if verified, false otherwise
   * 
   * @example
   * ```typescript
   * this.otpService.isVerified('user@example.com').subscribe(verified => {
   *   if (verified) {
   *     console.log('User has verified OTP');
   *   }
   * });
   * ```
   *
   * @remarks
   * Requires an authenticated Bearer token — `GET /api/otp/status/:recipient` is
   * an auth-gated endpoint. If called unauthenticated (e.g. pre-login), the request
   * will return 401 and `catchError` will safely emit `false`.
   */
  isVerified(recipient: string): Observable<boolean> {
    return this.getOtpStatus(recipient).pipe(
      map(response => response.data.status === 'VERIFIED'),
      catchError(() => [false]) // If not found, assume not verified
    );
  }

  /**
   * Check if OTP is expired
   * 
   * @param recipient - Email or phone number
   * @returns Observable that emits true if expired, false otherwise
   */
  isExpired(recipient: string): Observable<boolean> {
    return this.getOtpStatus(recipient).pipe(
      map(response => response.data.status === 'EXPIRED'),
      catchError(() => [false])
    );
  }

  /**
   * Get remaining verification attempts
   * 
   * @param recipient - Email or phone number
   * @returns Observable that emits remaining attempts count
   * 
   * @example
   * ```typescript
   * this.otpService.getRemainingAttempts('user@example.com').subscribe(remaining => {
   *   console.log(`${remaining} attempts left`);
   * });
   * ```
   */
  getRemainingAttempts(recipient: string): Observable<number> {
    return this.getOtpStatus(recipient).pipe(
      map(response => response.data.maxAttempts - response.data.attempts),
      catchError(() => [0])
    );
  }

  /**
   * Get time until OTP expiration
   * 
   * @param recipient - Email or phone number
   * @returns Observable that emits milliseconds until expiration (negative if expired)
   * 
   * @example
   * ```typescript
   * this.otpService.getTimeUntilExpiration('user@example.com').subscribe(ms => {
   *   const minutes = Math.floor(ms / 60000);
   *   console.log(`${minutes} minutes remaining`);
   * });
   * ```
   */
  getTimeUntilExpiration(recipient: string): Observable<number> {
    return this.getOtpStatus(recipient).pipe(
      map(response => {
        const expiresAt = new Date(response.data.expiresAt).getTime();
        return expiresAt - Date.now();
      }),
      catchError(() => [0])
    );
  }

  /**
   * Send OTP for registration
   * 
   * @param recipient - Email or phone number
   * @param deliveryMethod - EMAIL or SMS
   * @returns Observable of send response
   */
  sendRegistrationOtp(recipient: string, deliveryMethod: OtpDeliveryMethod): Observable<SendOtpResponse> {
    return this.sendOtp({ recipient, deliveryMethod, purpose: 'registration' });
  }

  /**
   * Send OTP for login
   * 
   * @param recipient - Email or phone number
   * @param deliveryMethod - EMAIL or SMS
   * @returns Observable of send response
   */
  sendLoginOtp(recipient: string, deliveryMethod: OtpDeliveryMethod): Observable<SendOtpResponse> {
    return this.sendOtp({ recipient, deliveryMethod, purpose: 'login' });
  }

  /**
   * Send OTP for password reset
   * 
   * @param recipient - Email or phone number
   * @param deliveryMethod - EMAIL or SMS
   * @returns Observable of send response
   */
  sendPasswordResetOtp(recipient: string, deliveryMethod: OtpDeliveryMethod): Observable<SendOtpResponse> {
    return this.sendOtp({ recipient, deliveryMethod, purpose: 'password-reset' });
  }

  /**
   * Reset client-side rate limit hint
   * 
   * @remarks
   * Clears the last send time, allowing canSendOtp signal to return true.
   * Useful for testing or when you know server-side rate limit has reset.
   */
  resetRateLimitHint(): void {
    this.log('Resetting rate limit hint');
    this.lastSendTime.set(null);
  }
}
